import { useState } from "react";
import axios from "axios";
import { Loader2, Check } from "lucide-react";
import { API } from "@/config";
import { getTracking, trackLead } from "@/lib/tracking";

export const ChoiceStep = ({ id, title, options, value, onPick }) => (
  <fieldset>
    <legend className="font-bold text-ink text-base sm:text-lg mb-3">{title}</legend>
    <div className="grid gap-2.5">
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            key={o} type="button" onClick={() => onPick(o)}
            data-testid={`${id}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`flex items-center justify-between text-left px-4 py-3.5 rounded-xl border-2 font-semibold text-[14.5px] transition-[background-color,border-color,transform] active:scale-[0.98] ${active ? "border-plum bg-plum/10 text-plum" : "border-zinc-200 hover:border-plum/50 text-ink"}`}
          >
            {o}
            <span className={`w-5 h-5 rounded-full border-2 grid place-items-center ${active ? "border-plum bg-plum" : "border-zinc-300"}`}>{active && <Check className="w-3 h-3 text-white" />}</span>
          </button>
        );
      })}
    </div>
  </fieldset>
);

const validate = (d) => {
  const e = {};
  if (d.name.trim().length < 2) e.name = "Please enter your full name.";
  if (!/^[6-9]\d{9}$/.test(d.phone)) e.phone = "Enter a valid 10-digit mobile number starting with 6–9.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(d.email.trim())) e.email = "Enter a valid email address.";
  return e;
};

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className="block text-[13px] font-semibold text-ink mb-1">{label}</label>
    {children}
    {error && <p className="text-[12.5px] text-red-600 mt-1 font-medium" data-testid={`${id}-error`} role="alert">{error}</p>}
  </div>
);

const inputCls = (err) => `w-full rounded-xl border-2 px-3.5 py-3 text-[15px] outline-none transition-colors focus:border-plum ${err ? "border-red-400" : "border-zinc-200"}`;

export const DetailsStep = ({ data, setData, onDone }) => {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverErr, setServerErr] = useState("");
  const set = (k) => (e) => {
    const v = k === "phone" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value;
    setData((d) => ({ ...d, [k]: v }));
    if (errors[k]) setErrors((x) => ({ ...x, [k]: undefined }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(data);
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    setServerErr("");
    try {
      const res = await axios.post(`${API}/register`, { ...data, name: data.name.trim(), email: data.email.trim(), ...getTracking() });
      const first = res.data?.first_name || data.name.trim().split(" ")[0];
      sessionStorage.setItem("wc_first_name", first);
      trackLead();
      onDone(first);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerErr(Array.isArray(detail) ? (detail[0]?.msg || "").replace("Value error, ", "") : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-3.5" data-testid="details-form">
      <Field id="name" label="Full Name" error={errors.name}>
        <input id="name" autoComplete="name" value={data.name} onChange={set("name")} className={inputCls(errors.name)} placeholder="Your full name" data-testid="input-name" />
      </Field>
      <Field id="phone" label="WhatsApp Number" error={errors.phone}>
        <div className={`flex rounded-xl border-2 overflow-hidden focus-within:border-plum transition-colors ${errors.phone ? "border-red-400" : "border-zinc-200"}`}>
          <span className="px-3 grid place-items-center bg-zinc-100 font-semibold text-ink text-[15px]">+91</span>
          <input id="phone" type="tel" inputMode="numeric" autoComplete="tel-national" value={data.phone} onChange={set("phone")} className="flex-1 px-3 py-3 text-[15px] outline-none" placeholder="10-digit mobile number" data-testid="input-phone" />
        </div>
      </Field>
      <Field id="email" label="Email" error={errors.email}>
        <input id="email" type="email" autoComplete="email" value={data.email} onChange={set("email")} className={inputCls(errors.email)} placeholder="you@example.com" data-testid="input-email" />
      </Field>
      <Field id="city" label="City (optional)">
        <input id="city" autoComplete="address-level2" value={data.city} onChange={set("city")} className={inputCls(false)} placeholder="Your city" data-testid="input-city" />
      </Field>
      {serverErr && <p className="text-sm text-red-600 font-medium" role="alert" data-testid="form-server-error">{serverErr}</p>}
      <button type="submit" disabled={loading} data-testid="reserve-seat-button" className="cta-btn w-full rounded-2xl py-4 text-white font-extrabold text-base flex items-center justify-center gap-2 disabled:opacity-70">
        {loading && <Loader2 className="w-5 h-5 animate-spin" />}{loading ? "Reserving…" : "Reserve My Free Seat"}
      </button>
    </form>
  );
};
